import Cookies from "js-cookie";
import { Cache } from "src/helpers/cache";

export type HTTPClientConfig = {
  baseUrl?: string;
  /**
   * By default each HTTPClient instance will have separate cache.
   * To share cache across clients, pass global Cache instance.
   */
  cache?: Cache;
  formatError?: (res: Response, errorCode?: string, body?: unknown) => HTTPError;
  /** Called after an HTTPError is constructed, before it is thrown. Use for cross-cutting concerns like global event dispatch. */
  interceptError?: (err: HTTPError) => void;
};

/**
 * Generic HTTP Client with centralized error handling
 *
 * In order to extend default Error class (e.g. with error messages specific to certain endpoints/features),
 * pass a function constructing a subclass of HTTPError.
 * Example:
 * ```ts
 * class FeatureError extends HTTPError {
 *   get humanMessage() {
 *     switch (this.errorCode) {
 *       case "feature-err-code-1":
 *         return "Feature failed";
 *
 *       default:
 *         return super.humanMessage;
 *     }
 *   }
 *   static fromResponse(res: Response, errorCode: string = "unknown") {
 *     return new FeatureError(res.statusText, errorCode)
 *   }
 * }
 *
 * // ...
 * new HTTPClient({ formatError: FeatureError.fromResponse })
 * ```
 */
export class HTTPClient {
  private baseUrl: string;
  private formatError: HTTPClientConfig["formatError"];
  private interceptError: HTTPClientConfig["interceptError"];
  private cache: Cache;

  constructor({
    baseUrl = "/server",
    cache = new Cache(),
    formatError = HTTPError.fromResponse,
    interceptError = exposeGlobalLoginErrors,
  }: HTTPClientConfig) {
    this.baseUrl = baseUrl;
    this.cache = cache;
    this.formatError = formatError;
    this.interceptError = interceptError;
  }

  get(url: string, options: Options = {}, init: RequestInit = {}) {
    return this.request("GET", url, options, init);
  }

  post(url: string, options: Options = {}, init: RequestInit = {}) {
    return this.request("POST", url, options, init);
  }

  put(url: string, options: Options = {}, init: RequestInit = {}) {
    return this.request("PUT", url, options, init);
  }

  patch(url: string, options: Options = {}, init: RequestInit = {}) {
    return this.request("PATCH", url, options, init);
  }

  delete(url: string, options: Options = {}, init: RequestInit = {}) {
    return this.request("DELETE", url, options, init);
  }

  async *stream<T = unknown>(
    url: string,
    { body, search, csrf, baseUrl }: Options = {},
    init: RequestInit = {},
  ): AsyncGenerator<T> {
    const sendCsrf = csrf ?? false;
    const reqInit: RequestInit = {
      ...init,
      method: "POST",
      credentials: init.credentials ?? "include",
      body: HTTPClient.normalizeBody(body),
      headers: {
        ...(HTTPClient.isPlainObject(body) ? { "Content-Type": "application/json" } : {}),
        ...(sendCsrf ? { "X-CSRF-Token": Cookies.get("csrf_token") } : {}),
        ...init.headers,
      },
    };
    const params = HTTPClient.normalizeSearchParams(search);
    const reqBaseUrl = typeof baseUrl === "string" ? baseUrl : this.baseUrl;
    const response = await fetch(`${reqBaseUrl}${url}${params}`, reqInit);

    if (!response.ok) {
      const text = await response.text();
      throw this.errorFor(response, HTTPClient.tryParseJson(text));
    }
    if (!response.body) throw this.buildError(response);

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const data = HTTPClient.parseSseData(frame);
          if (data === null) continue;
          try {
            yield JSON.parse(data) as T;
          } catch {
            console.error("[HTTPClient] could not parse SSE frame", data);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private async request(
    method: string,
    url: string,
    { body, search, responseType, csrf, baseUrl, cache }: Options,
    init: RequestInit = {},
  ) {
    const sendCsrf = csrf ?? method !== "GET";
    const options: RequestInit = {
      ...init,
      body: HTTPClient.normalizeBody(body),
      method,
      headers: {
        ...(HTTPClient.isPlainObject(body)
          ? { "Content-Type": "application/json" }
          : {}),
        ...(sendCsrf ? { "X-CSRF-Token": Cookies.get("csrf_token") } : {}),
        ...init.headers,
      },
    };
    const params = HTTPClient.normalizeSearchParams(search);
    const reqBaseUrl = typeof baseUrl === "string" ? baseUrl : this.baseUrl;
    const fetchUrl = `${reqBaseUrl}${url}${params}`;
    const cacheKey =
      typeof cache === "string" ? cache : fetchUrl;

    const execute = () =>
      fetch(fetchUrl, options).then((res) =>
        this.parseResponse(res, responseType),
      );

    if (!cache) return execute();

    const value = this.cache.get(cacheKey) || execute();
    this.cache.set(cacheKey, value);

    return value;
  }

  clearCache(cacheKey: string) {
    return this.cache.clear(cacheKey);
  }
  clearCacheByPrefix(cacheKey: string) {
    return this.cache.clearByPrefix(cacheKey);
  }

  private parseResponse<T>(
    response: Response,
    responseType: Options["responseType"],
  ) {
    if (!responseType || responseType === "json")
      return this.parseJsonResponse(response);

    return response[responseType]();
  }

  private async parseJsonResponse(response: Response) {
    // Even though this is used when expected response if in json format,
    // some endpoints return json body but still set `Content-Type: text/html` (e.g. /sayhi/).
    // We also cannot naively call `res.json()`, since some errors are in plain text.
    const text = await response.text();
    const body = HTTPClient.tryParseJson(text);

    // Successful json responses OR errors with status 200
    if (response.ok && body !== null) {
      if (!body || typeof body !== "object") return body;
      if ("errno" in body) throw this.buildError(response, body.errno, body);

      return body;
    }

    // 204 No Content OR unexpected text response
    if (response.ok && body === null) return {};

    // 4xx, 5xx
    throw this.errorFor(response, body);
  }

  // Maps a Response + its already-parsed body (possibly null) to an HTTPError. Shared by
  // parseJsonResponse and stream() so the error-code extraction lives in one place. Sync
  // (takes the parsed body) so callers that already consumed response.text() don't re-read it.
  private errorFor(response: Response, body: unknown): HTTPError {
    if (body === null) return this.buildError(response);
    if (typeof body !== "object") return this.buildError(response, undefined, body);
    if ("code" in body) return this.buildError(response, (body as { code?: string }).code, body);
    if ("errno" in body) return this.buildError(response, (body as { errno?: string }).errno, body);
    return this.buildError(response, undefined, body);
  }

  private buildError(res: Response, errorCode?: string, body?: unknown): HTTPError {
    const err = this.formatError(res, errorCode, body);
    this.interceptError?.(err);
    return err;
  }

  private static normalizeBody(body: Options["body"]): BodyInit {
    if (body === undefined) return null;
    if (HTTPClient.isPlainObject(body)) return JSON.stringify(body);

    return body;
  }

  private static normalizeSearchParams(search: Options["search"]): string {
    if (!search) return "";
    if (search instanceof URLSearchParams) return `?${search}`;

    const params = new URLSearchParams(search);

    return `?${params}`;
  }

  private static isPlainObject(a: unknown): a is object {
    return (
      a !== null &&
      typeof a === "object" &&
      (Object.getPrototypeOf(a) === Object.prototype ||
        Object.getPrototypeOf(a) === null ||
        Array.isArray(a))
    );
  }

  private static tryParseJson(text: string) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  // Returns the joined `data:` payload of one SSE event block (multiple data: lines joined with
  // "\n"), or null if the block carries no data line. Other lines (event:, id:, comments) are ignored.
  private static parseSseData(frame: string): string | null {
    const data = frame
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.replace(/^data:\s?/, ""));
    return data.length ? data.join("\n") : null;
  }
}

export type Options = {
  /** plain objects/arrays will be stringified, built-ins like FormData will be passed as-is */
  body?: BodyInit | object;
  /** url search parameters */
  search?: ConstructorParameters<typeof URLSearchParams>[0];
  /** change how the response is parsed, json by default  */
  responseType?: "json" | "text" | "arrayBuffer" | "blob" | "formData";
  /** Attach `X-CSRF-Token` header read from cookie. Defaults to `true` for non-GET methods, `false` for GET. */
  csrf?: boolean;
  /** overwrite url prefix, pass '' to disable the default */
  baseUrl?: string;
  /**
   * Enables request caching for requests. By default `false`.
   * If set to `true` url (including search params) will be used as cache key.
   * Other `string` values can be passed as alternative cache key e.g. for easier cache invalidation.
   */
  cache?: boolean | string;
};

export function exposeGlobalLoginErrors(err: HTTPError) {
  if (err.errorCode === "mfa_required")
    window.dispatchEvent(new CustomEvent("mfa-required"));
}

export class HTTPError extends Error {
  constructor(
    public message: string,
    public errorCode: string = "unknown",
  ) {
    super(message);
  }

  static fromResponse(res: Response, errorCode: string = "unknown") {
    return new HTTPError(res.statusText, errorCode);
  }

  get humanMessage(): string {
    /**
     * Only place common error code and messages here.
     * For specific features create a subclass implementing it's own `humanMessage` getter,
     * (and call `super.humanMessage` as `switch -> default`)
     */
    switch (this.errorCode) {
      case "not-logged":
        return "User is not logged in";

      case "access-denied":
        return "Your account has insufficient privileges";

      default:
        return "Unknown error occurred";
    }
  }
}
